/**
 * What the Facilities page reads, tied to the filter bar the way the Overview's bars are:
 * only people in scope, and —
 * while the Weighted claims switch is on — each person × department × facility's
 * claims scaled by that cell's weighted ÷ standard claims in the crosstab, which is
 * where the switch's weights land (weightedCrosstab.ts). The weights are not split by
 * month, so each month of a cell is scaled alike.
 *
 * The claim filters (claim type, service type, status) are EXACT on a table split by
 * claim cell (`FacilityMonthsDict.cells`, 2026-10-09): they select the rows of the
 * cells they pick, as they select each person's cells (claimCells.ts). On an older
 * table they stay modelled: each person's rows scaled by `Scope.claimShare`.
 *
 * The month range is NOT a scaling here, unlike the crosstab's `rangeShare`: this grain
 * has months, so a range simply reads its own months.
 */
import { useMemo } from 'react'
import { useAppData } from './AppData'
import { useLoadedDataset } from './DatasetProvider'
import { applyFilters, hasModeled, useFilters } from './filters'
import { claimSplitOf, selectedCells } from './claimCells'
import type { FacilityMonthsDict } from './types'
import {
  facilityCellKey,
  facilityRowWeight,
  homeOf,
  inFacilityGroups,
  type FacilityRow,
  type RowWeight,
} from '@/measures/facilities'

export interface FacilityData {
  fm: FacilityMonthsDict | null
  /** The range's months, as every other figure reads them. */
  months: string[]
  weightOf: (row: FacilityRow) => RowWeight | null
  /**
   * `weightOf` for a chosen set of people instead of the filter bar's: the same claim
   * filters, Facility group filter and Weighted switch. The person page's facility
   * table reads one person, and each facility's total over everyone in the table —
   * which the API has already cut to the caller's grant (docs/ACCESS.md).
   */
  weightFor: (people: readonly string[]) => (row: FacilityRow) => RowWeight | null
  /**
   * What a claim filter still models here, or null: everything but billed claims and
   * labor on a table not split by cell, weighted claims alone on one that is.
   */
  modeled: 'all' | 'weighted' | null
  /** Each facility's group and state (most claims wins). */
  groupOf: Map<string, string>
  stateOf: Map<string, string>
}


export function useFacilityData(): FacilityData {
  const dataset = useLoadedDataset()
  const { derived, scope, crosstab, claimBasis } = useAppData()
  const { filters } = useFilters()
  const fm = dataset.facilityMonths ?? null
  const split = useMemo(() => claimSplitOf(dataset), [dataset])

  // Standard vs weighted claims per person × department × facility, off the crosstab.
  const weighted = useMemo(() => {
    if (claimBasis !== 'weighted') return null
    const raw = new Map<string, number>()
    const adj = new Map<string, number>()
    const add = (m: Map<string, number>, ct: typeof crosstab) => {
      for (const r of ct.rows) {
        const k = facilityCellKey(ct.employees[r[0]]!, ct.departments[r[1]]!, ct.facilities[r[5]]!)
        m.set(k, (m.get(k) ?? 0) + r[6])
      }
    }
    add(raw, dataset.crosstab)
    add(adj, crosstab)
    const out = new Map<string, number>()
    for (const [k, n] of raw) if (n > 0) out.set(k, (adj.get(k) ?? n) / n)
    return out
  }, [claimBasis, dataset.crosstab, crosstab])

  const filtering = hasModeled(filters)
  // Which cells the claim filters pick, when the table is split by them.
  const sel = useMemo(
    () => (filtering && fm?.cells ? selectedCells({ cells: fm.cells }, filters) : null),
    [filtering, fm, filters],
  )

  /*
   * The Facility group filter selects this table's rows by their own group, exactly.
   * Its people are the scope's with that filter off: scaling them to the groups too
   * (Scope.people, GROUP_NOTE) would count it twice, and choosing them by it would
   * drop anyone the crosstab and this table file differently.
   */
  const groups = filters.facilityGroup
  const people = useMemo(
    () =>
      groups.length === 0
        ? scope.people
        : applyFilters(derived, dataset.composition, { ...filters, facilityGroup: [] }, split, crosstab).people,
    [groups.length, scope.people, derived, dataset.composition, filters, split, crosstab],
  )
  const weightFor = useMemo(
    () => (who: readonly string[]) =>
      inFacilityGroups(facilityRowWeight({ people: who, claimShare: scope.claimShare, sel, weighted }), groups),
    [scope.claimShare, weighted, sel, groups],
  )
  const weightOf = useMemo(() => weightFor(people.map((p) => p.employee)), [weightFor, people])

  const { groupOf, stateOf } = useMemo(() => {
    const ct = dataset.crosstab
    const byGroup: { facility: string; key: string; claims: number }[] = []
    const byState: { facility: string; key: string; claims: number }[] = []
    for (const r of ct.rows) {
      const facility = ct.facilities[r[5]]!
      // A hair over the claims, so a facility with labor and no claims still gets a home.
      byGroup.push({ facility, key: ct.facilityGroups[r[4]]!, claims: r[6] + 1e-9 })
      byState.push({ facility, key: ct.states[r[2]]!, claims: r[6] + 1e-9 })
    }
    if (fm)
      for (const r of fm.rows)
        byGroup.push({ facility: fm.facilities[r[3]]!, key: fm.facilityGroups[r[2]]!, claims: 1e-12 })
    return { groupOf: homeOf(byGroup), stateOf: homeOf(byState) }
  }, [dataset.crosstab, fm])

  const modeled = !filtering ? null : !sel ? 'all' : weighted ? 'weighted' : null
  return { fm, months: derived.months, weightOf, weightFor, modeled, groupOf, stateOf }
}

/**
 * Said wherever a claim filter still models something on this table (`modeled`): on a
 * table not split by claim cell, everything; on one that is, weighted claims only.
 */
export const FACILITY_MODELED: Record<'all' | 'weighted', string> = {
  all: ' Under the claim filters these are modelled, as the Overview’s facility bars are: each person’s facility rows are scaled by their share of the filtered claims.',
  weighted:
    ' Under the claim filters every figure is exact except weighted claims: each person’s weighting at a facility is not split by claim type, so it applies alike to the claims picked.',
}
